import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { JsonEditorOptions } from 'ang-jsoneditor';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Observable, Subject } from 'rxjs';
import { RRIAuthService } from '../../../common/interfaces/auth-service.interface';
import { RRIFileService } from '../../../common/interfaces/file-service.interface';
import { File } from '../../../common/models/file.model';
import { RRSpinnerModel } from '../../../spinner/models/spinner.model';
import { RR_WEBVIEWER_FILE_STATES } from '../../models/webviewer-file-states.model';
import { RRWebviewerFile } from '../../models/webviewer-file.model';
import { RRWebviewerSettings } from '../../models/webviewer-settings.model';

@Component({
  selector: 'rr-lightbox',
  templateUrl: './lightbox.component.html',
  styleUrls: ['./lightbox.component.scss'],
})
export class RRLightboxComponent implements OnInit {
  public jsonEditorOptions: JsonEditorOptions;
  public activeFile: File;
  public editorType: string; // 'json', '3d', 'text'
  public file: string;
  public jsonData: {};
  public subtitle: string;
  public btnNextDisabled: boolean;
  public btnPrevDisabled: boolean;
  public btnNextHide: boolean;
  public btnPrevHide: boolean;
  public objConfiguration: {};
  public fileExtension: string;
  public jsonFile: RRWebviewerFile;
  public baseurl: string;
  public obj: string;
  public canvasId: string;
  public spinnerStatus: RRSpinnerModel;
  public resizeing: Subject<boolean>;
  public webviewerSettings: RRWebviewerSettings;
  public showControlls: boolean;
  public controllerCounter: number = 0;
  public imageTooltip: string = '';

  @Input() authService: RRIAuthService;
  @Input() fileService: RRIFileService<any>;
  @Input() baseViewerUrl: string;
  @Input() renderServerUrl: string;
  @Input() showImageStats?: boolean = false;
  @Input() onButtonNextDisabled = new EventEmitter<boolean>();
  @Input() onButtonPrevDisabled = new EventEmitter<boolean>();
  @Input() onHidePrevButton = new EventEmitter<boolean>();
  @Input() onHideNextButton = new EventEmitter<boolean>();
  @Input() setSubtitle = new EventEmitter<string>();
  @Input() setActiveFile = new EventEmitter<string>();
  @Input() hideLoader = new EventEmitter<boolean>();
  @Input() showLoader = new EventEmitter<boolean>();
  @Output() clickedNext = new EventEmitter<boolean>();
  @Output() clickedPrev = new EventEmitter<boolean>();

  constructor(public modalRef: BsModalRef, private cdRef: ChangeDetectorRef) {
    this.editorType = 'image';
    this.jsonData = {};
    this.subtitle = '';
    this.btnNextDisabled = false;
    this.btnPrevDisabled = false;
    this.btnNextHide = false;
    this.btnPrevHide = false;
    this.objConfiguration = {};
    this.baseurl = '';
    this.canvasId = 'canvas3d-lightbox';
    this.obj = '';
    this.resizeing = new Subject<boolean>();
    this.spinnerStatus = {
      show: false,
      text: '',
    };

    this.webviewerSettings = {
      canvasId: this.canvasId,
      wireframeOption: true,
      loadingSpinner: { show: true, text: 'Loading 3d Model...' },
    };

    this.jsonEditorOptions = new JsonEditorOptions();
    this.jsonEditorOptions.mode = 'view';
    this.jsonEditorOptions.modes = ['view']; // set all allowed modes // 'code', 'text', 'tree', 'view'
    this.jsonEditorOptions.name = 'Root';
    this.jsonEditorOptions.enableSort = false;
    this.jsonEditorOptions.enableTransform = false;
  }

  ngOnInit(): void {
    this.onButtonNextDisabled.subscribe((data: boolean) => {
      this.btnNextDisabled = data;
    });
    this.onButtonPrevDisabled.subscribe((data: boolean) => {
      this.btnPrevDisabled = data;
    });
    this.onHideNextButton.subscribe((data: boolean) => {
      this.btnNextHide = data;
    });
    this.onHidePrevButton.subscribe((data: boolean) => {
      this.btnPrevHide = data;
    });

    this.setSubtitle.subscribe((text: string) => {
      this.subtitle = text;
    });

    this.hideLoader.subscribe((close: boolean) => {
      if (close) {
        this.closeLoader();
      }
    });
    this.showLoader.subscribe((open: boolean) => {
      if (open) {
        this.spinnerStatus = {
          show: true,
          text: '',
        };
      }
    });

    this.setActiveFile.subscribe((activeFile: File) => {
      if (activeFile.mimeType && typeof activeFile.mimeType !== 'undefined') {
        if (activeFile.mimeType.includes('image')) {
          this.editorType = 'image';
        } else if (activeFile.mimeType.includes('json')) {
          this.editorType = 'json';
          this.jsonData = JSON.parse(activeFile.text);
        } else if (activeFile.mimeType.includes('text')) {
          this.editorType = 'text';
        } else if (activeFile.mimeType.includes('pictofit-web')) {
          this.authService.getToken$().subscribe((token: string) => {
            this.jsonFile = new RRWebviewerFile(
              activeFile.url,
              RR_WEBVIEWER_FILE_STATES.JSON_SINGLE,
              this.baseViewerUrl,
              token,
            );
          });

          this.editorType = 'json3d';
        } else if (
          activeFile.mimeType.includes('obj') &&
          typeof activeFile['configurationResultId'] !== 'undefined' &&
          activeFile.url
        ) {
          this.jsonFile = new RRWebviewerFile(
            { obj: activeFile.url },
            RR_WEBVIEWER_FILE_STATES.CONVERT_JSON,
          );

          this.editorType = 'obj';
        } else if (activeFile.mimeType.includes('obj')) {
          this.authService.getToken$().subscribe((token: string) => {
            this.jsonFile = new RRWebviewerFile(
              { obj: activeFile.id },
              RR_WEBVIEWER_FILE_STATES.CONVERT_JSON,
              this.baseViewerUrl,
              token,
            );
          });

          this.editorType = 'obj';
        } else if (
          activeFile.mimeType.includes('model/gltf-binary') &&
          typeof activeFile['configurationResultId'] !== 'undefined' &&
          activeFile.url
        ) {
          this.jsonFile = new RRWebviewerFile(
            { obj: activeFile.url },
            RR_WEBVIEWER_FILE_STATES.CONVERT_JSON,
          );
          this.editorType = 'obj';
        } else {
          this.editorType = 'image';
        }
      } else if (typeof activeFile.url !== 'undefined' && activeFile.url) {
        let extension = activeFile.url;
        if (extension.indexOf('?') > -1) {
          extension = extension.substring(0, extension.indexOf('?'));
        }
        extension = extension.substr(
          extension.lastIndexOf('.'),
          extension.length,
        );

        if (extension == '.jpg') {
          this.editorType = 'image';
        } else if (extension == '.garment') {
          this.editorType = 'garment';
          this.fileExtension = 'garment';
        } else if (extension == '.avatar') {
          this.editorType = 'avatar';
          this.fileExtension = 'avatar';
        }
      }
      this.activeFile = activeFile;
      this.cdRef.detectChanges();
      this.setBodyClass();
    });
  }

  public onResizing(): void {
    this.resizeing.next(true);
  }

  public nextClick(): void {
    this.spinnerStatus = {
      show: true,
      text: '',
    };

    this.clickedNext.emit(true);
  }

  public prevClick(): void {
    this.spinnerStatus = {
      show: true,
      text: '',
    };
    this.clickedPrev.emit(true);
  }

  public closeLoader(): void {
    this.spinnerStatus.show = false;
    this.prepareImageTooltip();
  }

  public loadingSuccess(): void {
    console.log('Loading Successed');
    this.spinnerStatus.show = false;
  }

  public loadingStarted(): void {
    console.log('Loading Started');
    this.spinnerStatus = {
      show: true,
      text: 'Loading',
    };
  }

  public downloadFile(file: File): void {
    this.spinnerStatus = {
      show: true,
      text: 'Preparing download',
    };

    this.fileService.downloadFile(file).subscribe(() => {
      this.spinnerStatus.show = false;
    });
  }

  public showHideControlls() {
    this.showControlls = !this.showControlls;
  }

  public controllerNumber(counter: number) {
    this.controllerCounter = counter;
  }

  private setBodyClass(): void {
    document
      .getElementsByTagName('body')[0]
      .classList.remove('lightbox-type-image');
    document
      .getElementsByTagName('body')[0]
      .classList.remove('lightbox-type-json');
    document
      .getElementsByTagName('body')[0]
      .classList.remove('lightbox-type-obj');
    document
      .getElementsByTagName('body')[0]
      .classList.remove('lightbox-type-text');
    document
      .getElementsByTagName('body')[0]
      .classList.remove('lightbox-type-json3d');
    document
      .getElementsByTagName('body')[0]
      .classList.add('lightbox-type-' + this.editorType);

    this.resizeing.next(true);
  }

  private prepareImageTooltip() {
    this.imageTooltip = '';
    if (
      this.activeFile &&
      this.activeFile.url &&
      this.activeFile.mimeType.includes('image')
    ) {
      this.createImageTooltip(this.activeFile.url).subscribe((data) => {
        this.imageTooltip = data;
      });
    }
  }

  private createImageTooltip(url: string): Observable<string> {
    return new Observable((ob) => {
      const image = new Image();
      image.src = url;
      image.onload = (event: any) => {
        let height = 0;
        let width = 0;
        if (event.target) {
          height = event.target.naturalHeight;
          width = event.target.naturalWidth;
        } else if (event.path) {
          //fallback
          height = event.path[0].height;
          width = event.path[0].width;
        }

        ob.next(
          `Type: ${this.activeFile.mimeType}\nWidth: ${width}\nHeight: ${height}`,
        );
        ob.complete();
      };
    });
  }
}
